<template>
  <VDataGridV2
    :loader="dataLoader"
    paginated
    entity-type="order"
    group-children-property="positions"
    :group-context-menu="OrdersService.contextMenu"
    :context-menu="OrdersService.positionsContextMenu"
    :skeleton-row-count="16"
    :selectable="selectable"
    :selected-rows="selectedPositions"
    :disable-row-when="getRowDisabledReason"
    @group:dblclick="openOrderCard"
    @row:dblclick="openPositionCard"
    @update:selected-rows="onSelectedRowsUpdate"
  >
    <VDataGridGroupV2>
      <template #default="{ parent: row }">

        <CommentsButton
          entity-type-name="order"
          :entity="row"
          v-if="$core.currentPartner.isBuyer === true"
        />

        <div>
          <VLink @click.stop="openOrderCard(row)">
            №{{ $core.utils.string.formatWithDelimiter(row.id) }}
          </VLink>
          от {{ $core.utils.date.format(row.createdAt) }}
        </div>

        <span>{{ row.seller.name }}</span>

        <VBadges>
          <VBadge v-if="row.requisites.isSamples === true">Образцы</VBadge>
          <VBadge v-if="row.requisites.isSamples === true && row.requisites.isForTestsOnly === true">Не под план</VBadge>
          <VBadge v-if="$core.utils.isNullOrUndefined(row.notificationSentAt) === false">Уведомление отправлено {{ $core.utils.date.humanizeDate(row.notificationSentAt, 'HH|mm') }}</VBadge>
        </VBadges>
      </template>
    </VDataGridGroupV2>

    <!-- Колонки ниже повторяют реестр «В работе» (OrderPositionsRegistry) -->

    <VDataGridColumnV2
      v-if="$core.currentPartner.isBuyer === true"
      id="comment"
      content-class="comment"
    >
      <template #default="{ row }">
        <CommentsButton entity-type-name="orderPosition" :entity="row" />
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      id="id"
      width="100px"
      :filter="$core.filters.idPattern"
    >
      <template #label>
        №
        <div class="nowrap description">Заказ №</div>
      </template>
      <template #default="{ row }">
        <VLink @click.stop="openPositionCard(row)">
          {{ $core.utils.string.formatWithDelimiter(row.id) }}
        </VLink>
        <div v-if="$core.utils.isNullOrUndefined(row.order?.id) === false" class="description">
          {{ $core.utils.string.formatWithDelimiter(row.order.id) }}
        </div>
        <div class="nowrap description">
          {{ $core.utils.date.format(row.createdAt, 'DD.MM.yyyy') }}
        </div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      v-if="$core.currentPartner.isSeller === false"
      width="150px"
      content-class="ellipsis"
      :filter="$core.filters.seller"
      filter-by-value
    >
      <template #label>
        Грузоотправитель
        <div class="description">Юр лицо</div>
      </template>
      <template #default="{ row }">
        <div class="ellipsis" :title="row.seller.name">
          {{ row.seller.name }}
        </div>
        <div class="description ellipsis" :title="row.sellerCompany.name">
          {{ row.sellerCompany.name }}
        </div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      v-if="$core.currentPartner.isBuyer === false"
      width="150px"
      content-class="ellipsis"
    >
      <template #label>
        Грузополучатель
        <div class="description">Юр лицо</div>
      </template>
      <template #default="{ row }">
        <div class="ellipsis" :title="row.buyer.name">
          {{ row.buyer.name }}
        </div>
        <div class="description ellipsis" :title="row.buyerCompany.name">
          {{ row.buyerCompany.name }}
        </div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      id="userCode"
      label="Арт"
      width="70px"
      content-align="center"
      content-class="ellipsis"
      filterable
      filter-send-name="codePattern"
      :filter-props="codeFilterProps"
    >
      <template #default="{ row }">
        <VLink style="width: 100%" class="ellipsis" @click="OrdersService.showCodeInfoPopup(row, $event.currentTarget)">{{ row.code ?? '--' }}</VLink>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      id="product"
      content-class="ellipsis"
      width="300px"
      edit-material="info_outline"
      @edit="(row, anchor) => OrdersService.showPositionInfoPopup(row, anchor)"
    >
      <template #label>
        Товар
        <div class="description ellipsis">Производитель / материал</div>
      </template>
      <template #default="{ row }">
        <div class="ellipsis" :title="row.name">
          {{ row.name }}
        </div>
        <div
          v-if="row.isPrinting === true && $core.utils.isNullOrUndefined(row.material) === false"
          class="description ellipsis"
          :title="row.material"
        >
          Материал: {{ row.material }}
        </div>
        <div v-else-if="row.isRawMaterialsAndPackage === true && $core.utils.isNullOrUndefined(row.manufacturer) === false" class="description ellipsis" :title="row.manufacturer.name">
          Производитель: {{ row.manufacturer.name }}
        </div>
        <VBadges>
          <VBadge v-if="row.isSamples === true">Образцы</VBadge>
          <VBadge v-if="row.isSamples === true && row.isForTestsOnly === true">Не под план</VBadge>
          <VBadge v-if="row.isDraft === true">Черновик</VBadge>
        </VBadges>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      id="qty"
      width="140px"
      content-align="right"
    >
      <template #label>
        <div class="nowrap">Кол-во в работе</div>
        <div class="description">Всего</div>
      </template>
      <template #default="{ row }">
        <div class="nowrap">{{ row.shipmentStatus?.inWork?.formatSmart(row.unit) }}</div>
        <div class="description nowrap">{{ row.qty.formatSmart(row.unit) }}</div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      v-if="canViewSellerData === true"
      id="sumSeller"
      width="140px"
      content-align="right"
    >
      <template #label>
        Цена
        <div class="description">Сумма</div>
      </template>
      <template #default="{ row }">
        {{ row.priceFullSeller.format(row.currency, row.unit) }}
        <div class="description">{{ row.sumFullSeller.format(row.currency) }}</div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      v-if="canViewBuyerData === true"
      id="sumBuyer"
      width="140px"
      content-align="right"
    >
      <template #label>
        Цена покупателя
        <div class="description">Сумма</div>
      </template>
      <template #default="{ row }">
        {{ row.priceFullBuyer?.format(row.currency, row.unit) ?? '--' }}
        <div class="description">{{ row.sumFullBuyer?.format(row.currency) ?? '--' }}</div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      v-if="showStatusBarColumns === true"
      id="documentNumber"
      label="Отгрузка"
      width="130px"
      content-class="form"
    >
      <template #default="{ row }">
        <ShipmentStatusBar
          :shipment-status="row.shipmentStatus"
          :unit="row.unit"
          @open="(anchor) => OrdersService.showPositionShipmentsPopup(row, anchor)"
        />
      </template>
    </VDataGridColumnV2>

    <!-- Сортировка заказов по ближайшей дате доставки среди незавершённых позиций -->
    <VDataGridColumnV2
      id="desiredReceiptDate"
      label="Доставка"
      width="135px"
      header-class="nowrap"
      content-class="nowrap"
      content-align="center"
      sortable
      sort-default-value="asc"
      :filter="$core.filters.period('desiredReceiptDate')"
    >
      <template #default="{ row }">
        <span :class="{ critical: row.desiredReceiptDate?.isPast() === true }">
          {{ $core.utils.date.humanizeDate(row.desiredReceiptDate, null) }}
        </span>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      id="plannedShipmentDate"
      width="130px"
      content-align="center"
      content-class="ellipsis"
      :filter="$core.filters.period('plannedShipmentDate')"
    >
      <template #label>
        <div class="nowrap">Отгрузка</div>
        <div class="nowrap description">Тип доставки</div>
      </template>
      <template #default="{ row }">
        <div> {{ $core.utils.date.humanizeDate(row.plannedShipmentDate, null) }}</div>
        <div class="description ellipsis">{{ row.deliveryType.name }}</div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      v-if="showStatusBarColumns === true"
      id="payment"
      label="Оплата"
      width="180px"
      content-class="form"
    >
      <template #default="{ row }">
        <PaymentStatusBar
          :payment-status="row.paymentStatus"
          :currency="row.currency"
          @open="(anchor) => OrdersService.showPositionPaymentsPopup(row, anchor)"
        />
      </template>
    </VDataGridColumnV2>

    <VDataGridActionBarV2>
      <template #default="{ selectedRows }">
        <VButton
          v-if="selectable === false"
          theme="primary"
          :disabled="(dataLoader.data ?? []).length === 0"
          @click="selectable = true"
        >
          Выбрать позиции
        </VButton>

        <template v-else>
          <VLink style="margin-right: auto" @click="clearSelectedPositions">Отмена</VLink>
          <div class="nowrap">
            Выбрано {{ selectedRows.length }} строк{{ $core.utils.regional.ru.pluralize(selectedRows.length, 'а', 'и', '') }}
          </div>

          <VButton
            theme="primary"
            :disabled="assemblyDisabledError !== null"
            :title="assemblyDisabledError"
            @click="createAssemblyFromSelection"
          >
            Создать сборочный лист
          </VButton>
        </template>
      </template>
    </VDataGridActionBarV2>
  </VDataGridV2>
</template>

<script lang="ts">
import OrdersService     from '@/domains/Orders/OrdersService';
import Order             from '@/domains/Orders/Order';
import OrderPosition     from '@/domains/Orders/OrderPosition';
import AssembliesService from '@/domains/Assemblies/AssembliesService';
import Assembly          from '@/domains/Assemblies/Assembly';
import DataGridLoader    from '@/lib/components/VDataGridV2/DataGridLoader';
import type OrdersPageFilter from '@/domains/Orders/components/OrdersPage/OrdersPageFilter/OrdersPageFilter';
import type { UnwrapNestedRefs } from 'vue';
import ShipmentStatusBar from '@/domains/Orders/components/ShipmentStatusBar/ShipmentStatusBar.vue';
import PaymentStatusBar  from '@/domains/Orders/components/PaymentStatusBar/PaymentStatusBar.vue';

export default defineComponent({
  name: 'OrdersRegistryV2',

  components: {
    ShipmentStatusBar,
    PaymentStatusBar,
  },

  props: {
    /** Фильтры вкладки, уходят в orders.getOrdersList */
    params: {
      type:    Object as PropType<Record<string, unknown>>,
      default: () => ({}),
    },

    /** Фильтр страницы — сам инстанс, а не params: синхронен с фильтром колонки «Грузоотправитель» (см. OrderPositionsRegistry) */
    filter: {
      type:    Object as PropType<OrdersPageFilter | UnwrapNestedRefs<OrdersPageFilter>>,
      default: null,
    },
  },

  setup() {
    return {
      OrdersService,
      codeFilterProps: { placeholder: 'Артикул', clearable: true },
    };
  },

  data() {
    return {
      selectable:        false,
      selectedPositions: [] as OrderPosition[],

      dataLoader: new DataGridLoader(OrdersService.getOrdersListPaginated)
        .useExtraParams({ isOfferBased: false })
        .useReactiveSource(() => {
          return { ...this.params };
        })
        .useExternalFilter(() => this.filter as OrdersPageFilter | null),
    };
  },

  computed: {
    assemblyDisabledError(): string | null {
      return Assembly.getReasonCannotCreateFromOrderPositions(this.selectedPositions);
    },

    /** Право видеть данные стороны продавца */
    canViewSellerData(): boolean {
      return this.$core.currentPartner.isSeller === true || this.$core.currentPartner.isLSZ === true;
    },

    /** Право видеть данные стороны покупателя. ЛСЗ сюда не попадает — он смотрит цену продавца */
    canViewBuyerData(): boolean {
      return this.$core.currentPartner.isBuyer === true && this.$core.currentPartner.isLSZ === false;
    },

    /** Колонки с диаграммами оплаты и отгрузки — только админу или ЛСЗ */
    showStatusBarColumns(): boolean {
      return this.$core.currentPartner.isPrimaryBuyer === true || this.$core.currentUser.isAdmin === true;
    },
  },

  methods: {
    async openOrderCard(row: Order): Promise<void> {
      if (await OrdersService.showOrderCard(row)) {
        this.dataLoader.refresh();
      }
    },

    async openPositionCard(row: OrderPosition): Promise<void> {
      const submited = await this.$core.dialogs.OrderPositionCard.showAsPromise({ id: row.id });

      if (submited) {
        this.dataLoader.refresh();
      }
    },

    clearSelectedPositions(): void {
      this.selectedPositions = [];
      this.selectable = false;
    },

    onSelectedRowsUpdate(rows: OrderPosition[]): void {
      this.selectedPositions = rows;
    },

    async createAssemblyFromSelection(): Promise<void> {
      const positions = this.selectedPositions;

      this.clearSelectedPositions();

      await AssembliesService.showAssemblyCard(positions);
      this.dataLoader.refresh();
    },

    /**
     * Строку проверяем вместе с уже выбранными: войдёт ли она в одну сборку с ними.
     * Правила — в Assembly. Строка — причина, подпись поверх строки.
     */
    getRowDisabledReason(row: OrderPosition): string | false {
      if (this.selectable === false) {
        return false;
      }
      else {
        const positions = this.selectedPositions.includes(row) ? this.selectedPositions : [...this.selectedPositions, row];

        return Assembly.getReasonCannotCreateFromOrderPositions(positions) ?? false;
      }
    },
  },
});
</script>
